import React, { useCallback, useEffect, useRef, useState } from 'react';
import { RDS, RootState, identifyStrategy, strategy } from 'ouisys-engine/strategy';
import { useSelector } from 'react-redux';
import { Disclaimers, LanguageSwitcher, PricePoint, TopLegals, defineMessages } from 'ouisys-component-library';
import FLOWS from './__doNotModify/includedFlows';
import COMP from './__doNotModify/includedComponents';
import { useDispatch } from './__doNotModify/store';
import ProvidersWrapper from './providers/ProvidersWrapper';
import { FormattedMessage, legals } from './localization';
import Loader from './components/Loader';
import { useRootContext } from './providers/RootContext';
import { tracker } from 'ouisys-engine/utilities/tracker';
import PreLander from './components/PreLander';
import './Root.scss';
import './Bloxz.scss';

// error messages messages which might come from backend
const defaultMessages = defineMessages({
  InvalidMSISDN: {
    id: 'InvalidMSISDN',
    defaultMessage: 'This mobile number is incorrect, try again'
  },
  AlreadySubscribed: {
    id: 'AlreadySubscribed',
    defaultMessage: 'You are already a subscriber!'
  },
  UnknownError: {
    id: 'UnknownError',
    defaultMessage: 'An unknown error occurred.'
  },
  InvalidPIN: {
    id: 'InvalidPIN',
    defaultMessage: 'Please enter the correct PIN that you received on your phone'
  },
  SessionBlocked: {
    id: 'SessionBlocked',
    defaultMessage: 'Session Blocked!'
  },
  TooEarly: {
    id: 'TooEarly',
    defaultMessage: 'You must first submit a mobile number'
  },
  DidNotReceivePin: {
    id: 'DidNotReceivePin',
    defaultMessage: "Didn't receive PIN yet?"
  }
});

const Root = () => {
  const dispatch = useDispatch();
  const { currentState } = useSelector((state: RootState) => state.strategy);
  const { locale, switchLang, skipPrelander, phoneInputRef, setShake, d_theme, userType } = useRootContext();
  const [showOverlay, setShowOverlay] = useState(false);

  const creativeLogo = require("../src/assets/imgs/logo.svg");
  const creativePlayer = require("../src/assets/imgs/controller.svg");
  const creativePersuasive = require("../src/assets/imgs/persuasive.svg");

  const divFocus = useCallback(() => {
    tracker.customEvent('creativeArea', 'click', 'form-focus');

    // show overlay and shake #divFocus
    setShowOverlay(true);
    setShake(true);

    // focus on msisdn entry
    if (phoneInputRef.current) {
      phoneInputRef.current.focus();
    }

    // vibrate phone if availble
    if ((navigator?.userActivation?.hasBeenActive || navigator?.userActivation?.isActive) && "vibrate" in navigator) {
      navigator.vibrate([100, 30, 100, 30, 100]);
    }

    // reset shake effect after 1 second
    setTimeout(() => {
      setShowOverlay(false);
      setShake(false);
    }, 1234);
  }, []);

  const identifyStrategyEffect = useCallback(() => {
    dispatch(identifyStrategy());
  }, [dispatch]);

  useEffect(() => {
    identifyStrategyEffect();
  }, [identifyStrategyEffect]);

  return (
    <div className={`Bloxz ${locale === 'ar' ? 'direction-rtl' : ''} is-${userType}`}>

      <div id="overLay" className={`overlay ${showOverlay ? 'is-visible': 'is-hidden'}`} />

      <header className="Bloxz__header">
        <img src={creativeLogo} alt="" className='logo'/>
      </header>

      <div className="subscriptionAreaWrapper" onClick={(ev) => ev.stopPropagation()}>

        <div className="Bloxz__creative" onClick={divFocus}>
          <h1>Ver Ahora</h1>
          <h3>Obtenga acceso a todos los videos</h3>
        </div>

        {strategy({
          identifyInitialState: () => <Loader />,
          identifyFlowByMsisdn: (rds) => <COMP.IdentifyFlowByMsisdn rds={rds} />,
          identifyFlowByOperatorSelect: () => <COMP.OperatorSelection />,
          pinFlow: () => <FLOWS.PinFlow />,
          moFlow: () => <FLOWS.MoFlow />,
          moRedirFlow: () => <FLOWS.MoRedirFlow />,
          oneClickFlow: () => <FLOWS.OneClickFlow />,
          click2smsFlow: () => <FLOWS.Click2smsFlow />,
          ussdFlow: () => <FLOWS.UssdFlow />,
          creditCardFlow: () => <FLOWS.CreditCardFlow />
        })(currentState)}
        <img className="Bloxz__creative-controller" src={creativePlayer} alt='Controller' />
      </div>

      <img className="Bloxz__creative-persuasive" src={creativePersuasive} alt='Persuasive' />

      {RDS.WhenLoading(null, () => (
        <div className="loading-wrapper">
          <Loader />
          <div>
            <FormattedMessage id="PleaseWait" defaultMessage="Please wait..." />
          </div>
        </div>
      ))(currentState?.result as RDS.RemoteDataState<unknown, unknown>)}

      <div className="legal-area">
        <PricePoint className="price-point" locale={locale} legals={legals} />
        <Disclaimers className="disclaimer" locale={locale} legals={legals} />
      </div>

    </div>
  );
};

const R = () => (
  <ProvidersWrapper>
    <Root />
  </ProvidersWrapper>
);

export default R;
